<script lang="ts" module>
  export type CardDraft = { subject: string; body: string; generated_at?: string };
  export type CardSend = { subject: string; body: string; cc: string; files: File[] };
  export type CardAction = { label: string; onclick: () => void; disabled?: boolean };
  let cards = 0;
</script>

<script lang="ts">
  /* Fund Today Respond/Chase card. Markup, classes and CSS copy the Sales Today
     `.draft-card` (pages/Today.svelte draft cards) — change one, change the other. */
  import EmailBodyEditor from './EmailBodyEditor.svelte';
  import MultiSelect from './MultiSelect.svelte';
  import { plainToHtml } from './email_markdown';

  let {
    n, name, stage = null, subtitle, detail, severity = '',
    inbound = null, onExpand,
    draft = null, to = '', ccOptions = [], replySubject = '',
    emptyNote, draftLabel = '', busy = '',
    snoozeLabel = 'Snooze 7d', dismissLabel = 'Dismiss', dismissed = false, locked = false,
    extraActions = [],
    onSnooze, onDismiss, onUndo, onGenerate, onPolish, onSend, onDiscuss,
  }: {
    n: number;
    name: string;
    stage?: { label: string; color: string } | null;
    subtitle: string;
    detail: string;
    severity?: '' | 'red' | 'amber' | 'blue';
    inbound?: { label: string; text: string; loading: boolean; error: string } | null;
    onExpand?: () => void;
    draft?: CardDraft | null;
    to?: string;
    ccOptions?: string[];
    // A reply keeps its thread subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one.
    replySubject?: string;
    emptyNote: string;
    draftLabel?: string;
    busy?: '' | 'generate' | 'polish' | 'send';
    snoozeLabel?: string;
    dismissLabel?: string;
    dismissed?: boolean;
    // Disables Snooze and Dismiss while the owning record is being saved.
    locked?: boolean;
    extraActions?: CardAction[];
    onSnooze?: () => void;
    onDismiss: () => void;
    onUndo?: () => void;
    onGenerate?: () => Promise<CardDraft | null>;
    onPolish?: (mode: 'polish' | 'refine', subject: string, body: string) => Promise<{ subject: string; body: string; changes: string } | null>;
    onSend?: (draft: CardSend) => Promise<boolean>;
    onDiscuss?: (draft: CardDraft) => void;
  } = $props();

  const uid = `fund-card-${++cards}`;
  let open = $state(false);
  let inboundOpen = $state(false);
  let editing = $state(false);
  let subject = $state('');
  let body = $state('');
  let ccPicked = $state<string[]>([]);
  let ccOther = $state('');
  let files = $state<File[]>([]);
  let polishChanges = $state('');

  function relativeTime(iso: string | undefined): string {
    if (!iso) return '';
    const ts = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }
  function inboundIsLong(text: string): boolean {
    if (!text) return false;
    return text.length > 240 || (text.match(/\n/g) || []).length >= 4;
  }
  function toggle() {
    if (editing) return;
    open = !open;
    if (open) onExpand?.();
  }
  function startEdit(source: CardDraft) {
    subject = replySubject || source.subject;
    body = source.body;
    ccPicked = []; ccOther = ''; files = []; polishChanges = '';
    editing = true;
  }
  const cc = $derived([...ccPicked, ...ccOther.split(',').map(s => s.trim()).filter(Boolean)]
    .filter((address, i, all) => all.indexOf(address) === i).join(', '));
  const shown = $derived(editing ? { subject, body } : draft);

  async function generate() {
    const result = await onGenerate?.();
    if (result && editing) { body = result.body; if (!replySubject) subject = result.subject; polishChanges = ''; }
  }
  async function polish(mode: 'polish' | 'refine') {
    const result = await onPolish?.(mode, subject, body);
    if (!result) return;
    body = result.body;
    if (!replySubject) subject = result.subject;
    polishChanges = result.changes;
  }
  async function send() {
    const outgoing = editing ? { subject, body } : draft ? { subject: replySubject || draft.subject, body: draft.body } : null;
    if (!outgoing || !onSend) return;
    if (await onSend({ ...outgoing, cc: editing ? cc : '', files: editing ? files : [] })) editing = false;
  }
</script>

{#if dismissed}
  <article class="dismissed-back-tile">
    <div class="dismissed-back-text">
      <span class="dismissed-back-label">{name}</span>
      <span class="dismissed-back-status">{dismissLabel === 'Dismiss' ? 'Dismissed' : dismissLabel}</span>
    </div>
    {#if onUndo}<button class="today-mini-btn dismissed-undo-btn" onclick={onUndo}>↶ Undo</button>{/if}
  </article>
{:else}
<div class="ctask-swipe-wrap">
<!-- The header is the keyboard control; a click elsewhere on the card is a pointer shortcut. -->
<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
<article class="draft-card {severity ? `sev-${severity}` : ''}" class:open
         onclick={(e) => {
           if ((e.target as HTMLElement).closest('button, input, textarea, label, select, [contenteditable], [role="button"], .draft-inbound')) return;
           toggle();
         }}>
  <header class="draft-head" role="button" tabindex="0" aria-expanded={open}
          onclick={toggle}
          onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}>
    <span class="draft-num">{n}</span>
    <div class="draft-head-body">
      <div class="draft-head-row">
        <span class="draft-client">{name}</span>
        {#if stage}
          <span class="draft-stage-pill" style="color:{stage.color};border-color:color-mix(in srgb, {stage.color} 40%, transparent);">{stage.label}</span>
        {/if}
        <span class="draft-chev" class:open>›</span>
      </div>
      <div class="draft-subtitle">{subtitle}{#if draft?.generated_at}{' '}<span class="draft-meta-sep">·</span> drafted {relativeTime(draft.generated_at)}{/if}</div>
      <div class="draft-subject-line">{detail}</div>
    </div>
    {#if onSnooze || !open}
      <div class="draft-actions inline-right">
        {#if onSnooze}<button class="today-mini-btn" disabled={locked} onclick={(e) => { e.stopPropagation(); onSnooze(); }}>{snoozeLabel}</button>{/if}
        {#if !open}<button class="today-mini-btn ghost" disabled={locked} onclick={(e) => { e.stopPropagation(); onDismiss(); }}>{dismissLabel}</button>{/if}
      </div>
    {/if}
  </header>
  {#if open}
    {#if inbound}
      {@const long = inboundIsLong(inbound.text)}
      <div class="draft-inbound">
        <span class="draft-inbound-label">{inbound.label}</span>
        {#if inbound.loading}<span>Loading the synced message…</span>
        {:else if inbound.error}<span>{inbound.error}</span>
        {:else}
          <span class="draft-inbound-text" class:clamped={long && !inboundOpen}>{inbound.text}</span>
          {#if long}
            <button type="button" class="draft-inbound-more" onclick={(e) => { e.stopPropagation(); inboundOpen = !inboundOpen; }}>
              {inboundOpen ? 'Show less ▴' : 'Show more ▾'}
            </button>
          {/if}
        {/if}
      </div>
    {/if}
    {#if editing}
      <div class="draft-edit" role="presentation" onclick={(e) => e.stopPropagation()} onmousedown={(e) => e.stopPropagation()}>
        <span class="draft-edit-label">TO</span>
        <input value={to} readonly aria-label="To" />
        {#if ccOptions.length}<MultiSelect label="CC — LP contacts" options={ccOptions} bind:selected={ccPicked} />{/if}
        <label class="draft-edit-label" for="{uid}-cc">CC — Other (comma-separated)</label>
        <input id="{uid}-cc" bind:value={ccOther} placeholder="other@company.com" />
        <label class="draft-edit-label" for="{uid}-subject">Subject</label>
        <input id="{uid}-subject" bind:value={subject} readonly={!!replySubject} />
        <span class="draft-edit-label">Body</span>
        <EmailBodyEditor bind:value={body} rows={10} />
        <span class="draft-edit-label">Attachments</span>
        <div>
          {#if files.length}
            <div class="chip-row">
              {#each files as file, fi (fi)}
                <span class="chip selected">{file.name}
                  <button type="button" class="chip-x" aria-label="Remove {file.name}" onclick={() => files = files.filter((_, i) => i !== fi)}>&times;</button>
                </span>
              {/each}
            </div>
          {/if}
          <label class="today-mini-btn file-picker-btn">
            <span>+ Attach file</span>
            <input type="file" multiple class="visually-hidden-input"
                   onchange={(e) => { const input = e.currentTarget; if (input.files) files = [...files, ...Array.from(input.files)]; input.value = ''; }} />
          </label>
        </div>
        {#if polishChanges}
          <div class="draft-polish-changes">
            <div class="draft-polish-changes-title">Changes made</div>
            <pre>{polishChanges}</pre>
          </div>
        {/if}
      </div>
    {:else if shown}
      <div class="draft-subject">{replySubject || shown.subject}</div>
      <div class="draft-badge"><span class="draft-badge-tick">✦</span> DRAFT</div>
      <div class="draft-preview" role="button" tabindex="0" title="Click to edit"
           onclick={() => draft && startEdit(draft)}
           onkeydown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && draft) { e.preventDefault(); startEdit(draft); } }}>
        {@html plainToHtml(shown.body)}
      </div>
    {:else}
      <div class="draft-inbound"><span>{emptyNote}</span></div>
    {/if}
    <div class="draft-actions">
      {#if shown}
        {#if onSend}
          <button class="btn-primary" disabled={!!busy} onclick={send}>
            {busy === 'send' ? 'Sending…' : busy ? 'Working…' : 'Send'}
          </button>
        {/if}
        {#if editing}
          <button class="today-mini-btn" onclick={() => { editing = false; polishChanges = ''; }}>Cancel</button>
          <span class="divbar"></span>
          {#if onPolish}
            <button class="today-mini-btn draft-ai-btn" disabled={!!busy} onclick={() => polish('refine')}><span class="draft-spark">✦</span>{busy === 'polish' ? '…' : 'Refine content'}</button>
            <button class="today-mini-btn draft-ai-btn" disabled={!!busy} onclick={() => polish('polish')}><span class="draft-spark">✦</span>{busy === 'polish' ? '…' : 'Polish wording'}</button>
          {/if}
          {#if onGenerate}<button class="today-mini-btn draft-ai-btn" disabled={!!busy} onclick={generate}><span class="draft-spark">✦</span>{busy === 'generate' ? '…' : 'Regenerate'}</button>{/if}
        {:else}
          <button class="today-mini-btn" onclick={() => draft && startEdit(draft)}>Edit</button>
          <span class="divbar"></span>
        {/if}
        {#if onDiscuss}<button class="today-mini-btn draft-discuss-btn" onclick={() => onDiscuss({ subject: replySubject || shown.subject, body: shown.body })}>Discuss →</button>{/if}
      {:else}
        {#if onGenerate && draftLabel}
          <button class="today-mini-btn draft-ai-btn" disabled={!!busy} onclick={generate}><span class="draft-spark">✦</span>{busy === 'generate' ? 'Drafting…' : draftLabel}</button>
        {/if}
        {#each extraActions as action (action.label)}
          <button class="today-mini-btn draft-discuss-btn" disabled={action.disabled} onclick={action.onclick}>{action.label}</button>
        {/each}
      {/if}
      <button class="today-mini-btn ghost" disabled={locked} onclick={onDismiss}>{dismissLabel}</button>
    </div>
  {/if}
</article>
</div>
{/if}

<style>
  /* Copied from pages/Today.svelte (`.draft-card` family) — change one, change the other. */
  .ctask-swipe-wrap { position: relative; margin-bottom: 10px; overflow: hidden; border-radius: 12px; }
  .draft-card {
    position: relative; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: 12px; padding: 15px 18px; margin-bottom: 10px; display: flex;
    flex-direction: column; gap: 10px; overflow: hidden; transition: border-color 0.12s, background 0.12s;
  }
  .ctask-swipe-wrap > .draft-card { margin-bottom: 0; }
  .draft-card:hover { border-color: var(--border-strong); }
  .draft-card:not(.open):hover { background: var(--bg-hover); }
  .draft-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--green); }
  .draft-card.sev-red::before { background: var(--red); }
  .draft-card.sev-amber::before { background: var(--orange); }  /* DESIGN-SYSTEM §1 amber; Today.svelte still has #F5B042 */
  .draft-card.sev-blue::before { background: var(--blue); }
  .draft-head { display: flex; align-items: center; gap: 16px; cursor: pointer; user-select: none; }
  .draft-head:hover .draft-chev { color: var(--text-muted); }
  .draft-num {
    flex-shrink: 0; width: 22px; text-align: center; font-family: 'Fraunces', Georgia, serif;
    font-size: 22px; font-weight: 500; color: var(--text-dim); line-height: 1;
  }
  .draft-head-body { flex-grow: 1; min-width: 0; }
  .draft-head-row { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }
  .draft-client { font-size: 15px; font-weight: 600; color: var(--text); }
  .draft-stage-pill {
    font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 2px 6px; border: 1px solid; border-radius: 4px; background: transparent;
  }
  .draft-subtitle { margin-top: 2px; font-size: 12px; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .draft-meta-sep { color: var(--text-dim); opacity: 0.6; margin: 0 4px; }
  .draft-subject-line {
    margin-top: 1px; font-size: 11.5px; color: var(--text-dim); overflow: hidden;
    text-overflow: ellipsis; white-space: nowrap; font-style: italic;
  }
  .draft-inbound {
    background: var(--bg-hover); border-radius: 8px; padding: 10px 12px; font-size: 12.5px;
    color: var(--text-muted); line-height: 1.55; display: flex; flex-direction: column; gap: 6px;
  }
  .draft-inbound-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); }
  .draft-inbound-text { white-space: pre-wrap; }
  .draft-inbound-text.clamped { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 4; line-clamp: 4; overflow: hidden; }
  .draft-inbound-more {
    align-self: flex-start; background: transparent; border: none; padding: 0; cursor: pointer;
    font-size: 11px; font-weight: 600; color: var(--text-muted);
  }
  .draft-inbound-more:hover { color: var(--text); }
  .draft-subject { font-size: 14px; font-weight: 600; color: var(--text); margin-top: 2px; }
  .draft-badge {
    display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700;
    letter-spacing: 0.1em; color: var(--accent-ink); text-transform: uppercase; margin-top: -2px;
  }
  .draft-badge-tick { font-size: 11px; line-height: 1; }
  .draft-preview {
    font-size: 12.5px; line-height: 1.6; color: var(--text); background: var(--bg-hover);
    padding: 10px 12px; border-radius: 6px; border: 1px solid var(--border); cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
  }
  .draft-preview:hover { border-color: var(--border-strong); }
  .draft-preview:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .draft-preview :global(ul), .draft-preview :global(ol) { margin: 4px 0; padding-left: 20px; }
  .draft-preview :global(li) { margin: 0; }
  .draft-edit { display: flex; flex-direction: column; gap: 6px; }
  .draft-edit-label { font-size: 10.5px; color: var(--text-dim); letter-spacing: 0.06em; }
  .draft-edit input {
    width: 100%; font-family: inherit; font-size: 13px; padding: 8px 10px; border: 1px solid var(--border);
    border-radius: 5px; background: var(--bg); color: var(--text);
  }
  .draft-edit input:focus { outline: none; border-color: var(--border-strong); }
  .draft-edit input[readonly] { color: var(--text-muted); }
  .draft-polish-changes {
    margin-top: 6px; padding: 8px 10px; background: color-mix(in srgb, var(--accent) 6%, var(--bg-surface));
    border-left: 2px solid var(--accent); border-radius: 4px;
  }
  .draft-polish-changes-title {
    font-size: 10.5px; font-weight: 700; color: var(--text-dim); text-transform: uppercase;
    letter-spacing: 0.06em; margin-bottom: 4px;
  }
  .draft-polish-changes pre { margin: 0; font-family: inherit; font-size: 12px; color: var(--text); white-space: pre-wrap; word-break: break-word; }
  .file-picker-btn { display: inline-flex; align-items: center; gap: 4px; width: fit-content; }
  .visually-hidden-input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
  .chip-x { border: 0; background: none; color: inherit; cursor: pointer; padding: 0 0 0 4px; line-height: 1; }
  .draft-discuss-btn {
    background: transparent !important; border: 1px solid transparent !important; box-shadow: none !important;
    padding-left: 4px; padding-right: 4px; color: var(--accent-ink); transform: translateY(2px);
  }
  .draft-discuss-btn:hover {
    background: transparent !important; color: var(--accent-hover); text-decoration: underline; text-underline-offset: 2px;
  }
  .draft-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; row-gap: 6px; }
  .divbar { width: 1px; height: 20px; background: var(--border); margin: 0 2px; align-self: center; flex-shrink: 0; }
  .draft-actions .draft-ai-btn { background: transparent; border-color: transparent; font-weight: 400; gap: 3px; }
  .draft-actions .draft-ai-btn:hover { background: var(--bg-hover); border-color: transparent; color: var(--text); }
  .draft-actions:not(.inline-right) .today-mini-btn:not(.ghost):not(.draft-ai-btn):not(.draft-discuss-btn) { background: var(--bg-hover); }
  .draft-actions:not(.inline-right) > .today-mini-btn.ghost { margin-left: auto; }
  .draft-actions .btn-primary, .draft-actions .today-mini-btn {
    display: inline-flex; align-items: center; font-size: 12px; line-height: 1.4;
    padding-top: 5px; padding-bottom: 5px; box-sizing: border-box;
  }
  .draft-actions .draft-ai-btn { padding-left: 7px; padding-right: 7px; }
  .dismissed-back-tile {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px;
    border: 1px dashed var(--border); border-radius: 8px; background: color-mix(in srgb, var(--bg-surface) 60%, transparent);
    color: var(--text-dim); margin: 4px 0; transition: opacity 0.15s;
  }
  .dismissed-back-tile:hover { opacity: 1; border-color: var(--border-strong); }
  .dismissed-back-text { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
  .dismissed-back-label { font-weight: 600; color: var(--text-muted); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dismissed-back-status { font-size: 11px; font-style: italic; color: var(--text-dim); opacity: 0.7; }
  .dismissed-back-status::before { content: "·"; margin-right: 10px; opacity: 0.5; }
  .dismissed-undo-btn { flex-shrink: 0; }
  @media (max-width: 560px) {
    .draft-actions .today-mini-btn, .draft-actions .btn-primary { font-size: 11px; padding: 4px 8px; }
  }
  @media (max-width: 420px) {
    .draft-actions .today-mini-btn, .draft-actions .btn-primary { font-size: 10.5px; padding: 3px 6px; }
    .draft-actions { gap: 4px; row-gap: 4px; }
  }
</style>
